import {
  AYANTS_DROITS_BULK_MAX_IDS,
  type AyantDroitBulkResult,
  type AyantDroitBulkResultItem,
  type EmployeeBeneficiaryStatus,
} from '@cpce/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiPost } from '@/api/api.utils';
import { commiteeMutation, MutationMethod } from '@/api/mutations.utils';
import { queryClient } from '@/api/queries.utils';
import { CommiteeIdResolutionStatus, useCommiteeIdResolutionFromUrl } from '@/utils/router';
import { employeesQueryKeys } from '../employees/employees.query-keys';
import { employeesBeneficiariesQueryKeys } from './employees-beneficiaries.query-keys';

export function useSetEmployeeBeneficiaryLienType(id: number) {
  return commiteeMutation({
    url: `/commitee/:commiteeId/employees-beneficiaries/${id}/lien-type`,
    method: MutationMethod.PATCH,
    invalidateQueries: [employeesBeneficiariesQueryKeys.all, employeesQueryKeys.all],
    successMessage: 'Lien mis à jour',
    body: (status: EmployeeBeneficiaryStatus) => ({ status }),
  });
}

export function useSetEmployeeBeneficiaryValidationStatus(id: number) {
  return commiteeMutation({
    url: `/commitee/:commiteeId/employees-beneficiaries/${id}/validation-status`,
    method: MutationMethod.PATCH,
    invalidateQueries: [employeesBeneficiariesQueryKeys.all],
    body: (validated: boolean) => ({ validated }),
  });
}

function chunk<T>(items: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, (i + 1) * size)
  );
}

function plural(count: number) {
  return count > 1 ? 's' : '';
}

/**
 * The billetterie answers 200 to a statut change without applying it, so a blanket
 * "Opération réussie" would be a lie. Each bucket is reported for what it is.
 */
function reportOutcome(result: AyantDroitBulkResult, validated: boolean) {
  const { updated, ignored, failed } = result;
  const action = validated ? 'validé' : 'refusé';

  if (updated > 0) {
    toast.success(`${updated} ayant${plural(updated)} droit ${action}${plural(updated)}`);
  }
  if (ignored > 0) {
    toast.error(
      `${ignored} ayant${plural(ignored)} droit non ${action}${plural(ignored)} : la billetterie a accepté la demande sans l'appliquer.`,
      { duration: 10000 }
    );
  }
  if (failed > 0) {
    const firstMessage = result.items.find((item) => item.outcome === 'failed')?.message;
    toast.error(
      `${failed} ayant${plural(failed)} droit en échec${firstMessage ? ` : ${firstMessage}` : ''}`,
      { duration: 10000 }
    );
  }
  if (updated === 0 && ignored === 0 && failed === 0) {
    toast.error('Aucun ayant-droit traité');
  }
}

export interface BulkValidationProgress {
  done: number;
  total: number;
}

export function useBulkSetEmployeeBeneficiariesValidationStatus() {
  const commiteeIdResolution = useCommiteeIdResolutionFromUrl();
  const [progress, setProgress] = useState<BulkValidationProgress | null>(null);

  const mutation = useMutation({
    mutationFn: async (variables: { ids: number[]; validated: boolean }) => {
      if (commiteeIdResolution.status !== CommiteeIdResolutionStatus.READY) {
        throw new Error('Commitee ID is not available yet');
      }
      const url = `/commitee/${commiteeIdResolution.commiteeId}/employees-beneficiaries/bulk-validation-status`;
      const batches = chunk(variables.ids, AYANTS_DROITS_BULK_MAX_IDS);
      const items: AyantDroitBulkResultItem[] = [];

      setProgress({ done: 0, total: variables.ids.length });
      // Sequential: the API already parallelises inside a batch, and the billetterie
      // falls over on a wide fan-out.
      for (const batch of batches) {
        const result = await apiPost<AyantDroitBulkResult>(url, {
          ids: batch,
          validated: variables.validated,
        });
        items.push(...result.items);
        setProgress({ done: items.length, total: variables.ids.length });
      }

      return {
        updated: items.filter((item) => item.outcome === 'updated').length,
        ignored: items.filter((item) => item.outcome === 'ignored').length,
        failed: items.filter((item) => item.outcome === 'failed').length,
        items,
      } satisfies AyantDroitBulkResult;
    },
    onSettled: async () => {
      setProgress(null);
      await queryClient.invalidateQueries({ queryKey: employeesBeneficiariesQueryKeys.all });
    },
    onSuccess: (result, variables) => reportOutcome(result, variables.validated),
    onError: (error) => {
      toast.error(`L'opération a échoué: ${error instanceof Error ? error.message : 'Erreur'}`);
    },
  });

  return {
    bulkSetValidationStatus: mutation.mutateAsync,
    isPending: mutation.isPending,
    progress,
  };
}
